import { Box, Flex, Icon } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { useWidgets } from "../../hooks/useWidgets";
import { useAirQuality } from "../../hooks/useAirQuality";
import { useSunsetSunrise } from "../../hooks/useSunsetSunrise";
import { useYearProgress } from "../../hooks/useYearProgress";
import { WidgetCard } from "./WidgetCard";
import { AddWidgetModal } from "./AddWidgetModal";
import { useState } from "react";

const MotionBox = motion(Box);

const SectionContainer = styled(Box)<{ $theme: AppTheme }>`
  padding: 1rem 0 0.5rem 0;
  background: ${({ $theme }) => $theme.background};
  margin-top: 3.5rem; /* Większy margines od góry, żeby kafle dat nie najezdżały na widgety */
  position: relative;
`;

const FloatingAddButton = styled(motion.button)<{ $theme: AppTheme }>`
  position: absolute;
  bottom: -0.5rem;
  right: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $theme }) => $theme.primary};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${({ $theme }) => $theme.shadowLg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${({ $theme }) => $theme.shadowXl};
    background: ${({ $theme }) => $theme.brand || $theme.primary};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AddButtonContainer = styled(MotionBox)<{ $theme: AppTheme }>`
  position: relative;
  background: ${({ $theme }) => $theme.backgroundCard};
  border: 2px dashed ${({ $theme }) => $theme.border};
  border-radius: ${THEME.borderRadiusLg};
  padding: 0.75rem;
  box-shadow: ${({ $theme }) => $theme.shadowMd};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${({ $theme }) => $theme.shadowLg};
    transform: translateY(-2px);
    border-color: ${({ $theme }) => `${$theme.primary}40`};
    background: ${({ $theme }) => $theme.backgroundMuted};
  }
`;

type WidgetsSectionProps = {
  scheduleLength: number;
};

export function WidgetsSection({ scheduleLength }: WidgetsSectionProps) {
  const theme = useTheme() as AppTheme;
  const { widgets, addWidget, removeWidget, getAvailableTypes } = useWidgets();
  const { airQuality, isLoading } = useAirQuality();
  const { sunrise, sunset, dayLength } = useSunsetSunrise();
  const { percentage } = useYearProgress();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pokazuj loading tylko jeśli nie mamy danych z cache
  const showLoading = isLoading && !airQuality;

  // Pokaż sekcję tylko jeśli jest harmonogram
  if (scheduleLength === 0) {
    return null;
  }

  const availableTypes = getAvailableTypes();
  const hasWidgets = widgets.length > 0;
  const canAddMore = availableTypes.length > 0;
  // Ikona + tylko dla dokładnie 3 widgetów
  const showFloatingButton = widgets.length === 3 && canAddMore;
  // Kafelek dla mniej niż 3 widgetów LUB dla 4-5 widgetów
  const showTileButton = (widgets.length < 3 || (widgets.length >= 4 && widgets.length <= 5)) && canAddMore;

  return (
    <>
      <SectionContainer $theme={theme}>
        {showFloatingButton && (
          <FloatingAddButton
            $theme={theme}
            onClick={() => setIsModalOpen(true)}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Dodaj widget"
          >
            <Plus size={20} />
          </FloatingAddButton>
        )}
        
        <Flex
          direction="row"
          wrap="wrap"
          gap={3}
          align="stretch"
          justify={hasWidgets ? "flex-start" : "center"}
        >
          <AnimatePresence>
            {widgets.map((widget) => (
              <MotionBox
                key={widget.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                flex="0 0 calc((100% - 2 * 0.75rem) / 3)"
                minW="calc((100% - 2 * 0.75rem) / 3)"
                maxW="calc((100% - 2 * 0.75rem) / 3)"
                width="calc((100% - 2 * 0.75rem) / 3)"
                height="130px" /* Większa wysokość, żeby dane się mieściły */
              >
                <WidgetCard
                  widget={widget}
                  airQuality={airQuality}
                  onRemove={removeWidget}
                  isLoading={showLoading}
                  sunriseSunset={{ sunrise, sunset, dayLength }}
                  yearProgress={{ percentage }}
                />
              </MotionBox>
            ))}
          </AnimatePresence>

          {showTileButton && (
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              flex="0 0 calc((100% - 2 * 0.75rem) / 3)"
              minW="calc((100% - 2 * 0.75rem) / 3)"
              maxW="calc((100% - 2 * 0.75rem) / 3)"
              height="130px"
            >
              <AddButtonContainer
                $theme={theme}
                onClick={() => setIsModalOpen(true)}
                whileHover={{ y: -2 }}
              >
                <Flex direction="column" align="center" gap={1.5} flex={1} justify="space-between" minH={0}>
                  <Box
                    p={1.5}
                    borderRadius={THEME.borderRadius}
                    bg={`${theme.primary}15`}
                    color={theme.primary}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon as={Plus} boxSize={4} />
                  </Box>
                  
                  <Box
                    fontFamily="'Outfit', sans-serif"
                    fontSize="xs"
                    fontWeight="600"
                    color={theme.textMuted}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    flexShrink={0}
                    lineHeight="1.2"
                    textAlign="center"
                  >
                    Dodaj widget
                  </Box>
                  
                  <Box flex={1} minH={0} />
                </Flex>
              </AddButtonContainer>
            </MotionBox>
          )}
        </Flex>
      </SectionContainer>

      <AddWidgetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={addWidget}
      />
    </>
  );
}

